/**
 * Reports domain API (Spec 059, E2): report entries, correlation, browse tree and duplicates.
 *
 * What holds these together is feature cohesion, not read-only-ness: each is an aggregated view
 * over documents that is built, navigated and acted on as one screen, so its calls belong in one
 * module. Note that `unmarkDuplicate`/`unmarkDuplicatesBatch` are mutations (POST) — an earlier
 * version of this comment called the whole module "read-oriented", which was wrong and would have
 * invited further mutations to be filed here under a label that does not fit (Review Spec 059 F1).
 * Plain document mutations belong in `documents.ts`.
 *
 * Moved bitgleich from the former monolithic `api.ts` — no behavior change (E5).
 */
import type { ApiCore } from './core';
import type { Document } from './documents';

// === Correlation types ===

export interface DocumentCatalogEntry {
  id: string;
  fileName: string;
  displayName?: string;
  fileSize: number;
  mimeType: string;
  uploadedAt: string;
  referenceDate?: string;
  status: Document['status'];
  category?: string;
  subCategory?: string;
  slug?: string;
  thumbnailMedium?: string;
  correlationKeys?: string[];
}

export interface RelatedDocumentsResponse {
  documents: DocumentCatalogEntry[];
  documentId: string;
  totalCount: number;
  availableCorrelationTypes: string[];
  hasMore: boolean;
}

export interface CorrelationKeysResponse {
  keysByType: Record<string, string[]>;
}

export interface CorrelationEntitiesResponse {
  persons: { id: string; name: string; role?: string }[];
  properties: { address: string; documentCount: number }[];
}

// === Directory Tree types (Issue #70) ===

/** Filters of the directory tree — same names as the document list (Spec 119 E1). */
export interface DirectoryTreeParams {
  householdId?: string;
  category?: string;
  subCategory?: string;
  isReviewed?: boolean;
  search?: string;
  fromDate?: string;
  toDate?: string;
  fileFormat?: string;
  minSize?: number;
  maxSize?: number;
  assignedTo?: string;
  userTag?: string;
}

export interface DirectoryTreeResponse {
  tree: ResponsibleNode[];
}

export interface ResponsibleNode {
  responsible: string;
  documentCount: number;
  categories: CategoryNode[];
}

export interface CategoryNode {
  category: string;
  categoryDisplayName: string;
  categoryDisplayNameEn?: string;
  documentCount: number;
  subCategories: SubCategoryNode[];
}

export interface SubCategoryNode {
  subCategory: string;
  subCategoryDisplayName: string;
  subCategoryDisplayNameEn?: string;
  documentCount: number;
  documents: TreeDocument[];
}

export interface TreeDocument {
  id: string;
  fileName: string;
  displayName: string;
  slug?: string;
  referenceDate?: string;
  uploadedAt: string;
  // Spec 010: server-side pre-coalesced subject (subject → documentType, computed in BigQuery).
  // Seit Spec 010 liefert die API das Feld (DirectoryTreeQueryBuilder → DirectoryTreeBuilder).
  subject?: string;
}

// === Duplicates types ===

export interface DuplicateGroupsResponse {
  groups: DuplicateGroupDto[];
  totalDuplicates: number;
}

export interface DuplicateGroupDto {
  original: DuplicateDocumentDto;
  duplicates: DuplicateDocumentDto[];
}

export interface DuplicateDocumentDto {
  id: string;
  fileName: string;
  displayName?: string;
  slug?: string;
  fileSize: number;
  mimeType: string;
  uploadedAt: string;
  thumbnailMedium?: string;
}

export interface ReportFilterField {
  name: string;
  displayName: string;
  fieldType: string;
  required: boolean;
  options?: string[];
  defaultValue?: string;
}

export interface DataSourceInfo {
  label: string;
}

export interface ReportType {
  reportType: string;
  displayName: string;
  group?: string;
  groupDisplayName?: string;
  fields: ReportFilterField[];
  dataSources: DataSourceInfo[];
  defaultSortBy?: string;
  defaultSortDescending?: boolean;
}

export interface ReportEntryDto {
  id: string;
  documentId: string;
  reportType: string;
  pluginVersion: string;
  entryDate: string;
  year?: number;
  category: string;
  subCategory: string;
  taxCategory?: string;
  householdId?: string;
  persons: string[];
  properties: string[];
  data: Record<string, unknown>;
  createdAt: string;
}

export interface ReportQueryResponse {
  items: ReportEntryDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export class ReportsApi {
  constructor(private readonly core: ApiCore) {}

  /**
   * Get report entries for a specific document
   */
  async getDocumentReports(id: string): Promise<ReportEntryDto[]> {
    return this.core.fetchJson<ReportEntryDto[]>(
      `${this.core.baseUrl}/api/v1/documents/${id}/reports`,
      { headers: this.core.getAuthHeaders() },
    );
  }

  /**
   * Get available report types with filter schemas
   */
  async getReportTypes(): Promise<ReportType[]> {
    return this.core.fetchJson<ReportType[]>(`${this.core.baseUrl}/api/v1/reports/types`, {
      headers: this.core.getAuthHeaders(),
    });
  }

  /**
   * Query report entries by type with optional filters
   */
  async queryReports(
    reportType: string,
    params?: Record<string, string>,
  ): Promise<ReportQueryResponse> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value) queryParams.set(key, value);
      });
    }

    return this.core.fetchJson<ReportQueryResponse>(
      `${this.core.baseUrl}/api/v1/reports/${reportType}?${queryParams}`,
      { headers: this.core.getAuthHeaders() },
    );
  }

  /**
   * Export report entries as a ZIP archive (CSV + original PDFs).
   * Returns a Blob for download.
   */
  async exportReport(reportType: string, params?: Record<string, string>): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value) queryParams.set(key, value);
      });
    }

    const response = await this.core.fetchWithErrorHandling(
      `${this.core.baseUrl}/api/v1/reports/${reportType}/export?${queryParams}`,
      { headers: this.core.getAuthHeaders() },
    );
    return response.blob();
  }

  // === Correlation API ===

  /** Get documents related to a given document via correlation keys */
  async getRelatedDocuments(
    id: string,
    correlationType?: string,
    limit: number = 5,
    offset: number = 0,
  ): Promise<RelatedDocumentsResponse> {
    const params = new URLSearchParams();
    if (correlationType) params.set('correlationType', correlationType);
    params.set('limit', limit.toString());
    params.set('offset', offset.toString());

    return this.core.fetchJson<RelatedDocumentsResponse>(
      `${this.core.baseUrl}/api/v1/documents/${id}/related?${params}`,
      { headers: this.core.getAuthHeaders() },
    );
  }

  /** Get all correlation keys grouped by type */
  async getCorrelationKeys(): Promise<CorrelationKeysResponse> {
    return this.core.fetchJson<CorrelationKeysResponse>(
      `${this.core.baseUrl}/api/v1/correlation/keys`,
      { headers: this.core.getAuthHeaders() },
    );
  }

  /** Get known correlation entities (persons and properties) */
  async getCorrelationEntities(): Promise<CorrelationEntitiesResponse> {
    return this.core.fetchJson<CorrelationEntitiesResponse>(
      `${this.core.baseUrl}/api/v1/correlation/entities`,
      { headers: this.core.getAuthHeaders() },
    );
  }

  // === Browse / Directory Tree API ===

  /**
   * Hierarchical directory tree for the browse view (Issue #70). Takes the same filters as the
   * document list (Spec 119 E1); unset or empty values are left out of the query.
   */
  async getDirectoryTree(params: DirectoryTreeParams = {}): Promise<DirectoryTreeResponse> {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
    }
    const qs = query.toString();
    return this.core.fetchJson<DirectoryTreeResponse>(
      `${this.core.baseUrl}/api/v1/browse${qs ? '?' + qs : ''}`,
      { headers: this.core.getAuthHeaders() },
    );
  }

  // === Duplicates API ===

  /** Get duplicate document groups */
  async getDuplicateGroups(householdId?: string): Promise<DuplicateGroupsResponse> {
    const params = new URLSearchParams();
    if (householdId) params.set('householdId', householdId);
    const qs = params.toString();
    return this.core.fetchJson<DuplicateGroupsResponse>(
      `${this.core.baseUrl}/api/v1/duplicates${qs ? '?' + qs : ''}`,
      { headers: this.core.getAuthHeaders() },
    );
  }

  /** Remove duplicate marking from a document (keep as standalone) */
  async unmarkDuplicate(id: string): Promise<void> {
    return this.core.fetchVoid(`${this.core.baseUrl}/api/v1/duplicates/${id}/unmark`, {
      method: 'POST',
      headers: this.core.getAuthHeaders(),
    });
  }

  /**
   * Remove duplicate marking from multiple documents in a single batch request.
   * Returns unmarkedCount (effective) and totalRequested so callers can detect partial failures.
   */
  async unmarkDuplicatesBatch(
    ids: string[],
  ): Promise<{ unmarkedCount: number; totalRequested: number }> {
    return this.core.fetchJson(`${this.core.baseUrl}/api/v1/duplicates/unmark-batch`, {
      method: 'POST',
      headers: { ...this.core.getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ documentIds: ids }),
    });
  }
}
